import Close from '@mui/icons-material/Close'
import Description from '@mui/icons-material/Description'
import Download from '@mui/icons-material/Download'
import Print from '@mui/icons-material/Print'
import Send from '@mui/icons-material/Send'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import { getDataMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams, postSPDataWithParams, supportApiURL } from 'src/supportPortal/services/services'
import { sessionService } from 'src/consumer/components/LiveSessionPage/services/sessionService'
import { getCountryCode, getMainLanguageCode } from 'src/clinic/services'
import PrintableScreeningReportComponent from 'src/mentor/components/ScreeningReportMakingPage/printableReport/PrintableScreeningReportComponent'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'
import styled from 'styled-components'

const DrawerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 255, 0.98) 100%);
  backdrop-filter: blur(20px);
`

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(67, 56, 202, 0.1);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  @media print {
    display: none !important;
  }
`

const DrawerTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  color: rgba(67, 56, 202, 0.9);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
`

const ActionButtonsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DrawerContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(67, 56, 202, 0.3);
    }
  }
  
  @media print {
    padding: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    background: white !important;
    
    /* A4 page size for print */
    @page {
      size: A4;
      margin: 0.5cm;
    }
  }
`

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 20px;
  min-height: 200px;
`

const ErrorContainer = styled(Box)`
  padding: 20px;
`

const ReportContainer = styled(Box)`
  width: 100%;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  
  /* A4 size for view mode */
  max-width: 210mm; /* A4 width */
  margin-left: auto;
  margin-right: auto;
  
  /* A4 page styling */
  & > * {
    /* Ensure proper page breaks */
    page-break-inside: avoid;
  }
  
  /* Page break controls */
  h1, h2, h3, h4, h5, h6 {
    page-break-after: avoid;
  }
  
  table, img {
    page-break-inside: avoid;
  }
  
  @media print {
    padding: 0;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    max-width: 100%;
    width: 100%;
    
    /* A4 page size for printing */
    @page {
      size: A4;
      margin: 0.5cm;
    }
    
    /* Page break controls for print */
    & > * {
      page-break-inside: avoid;
    }
    
    h1, h2, h3, h4, h5, h6 {
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    
    table {
      page-break-inside: avoid;
    }
    
    img {
      page-break-inside: avoid;
      max-width: 100%;
    }
    
    /* Ensure content fits A4 */
    & > div {
      max-width: 210mm;
      margin: 0 auto;
    }
  }
`

const PrintOnlyContainer = styled(Box)`
  @media print {
    /* Hide drawer header, buttons, and UI elements */
    .report-drawer-header,
    .report-drawer-actions,
    & button,
    & [class*="MuiIconButton"],
    & [class*="MuiButton"] {
      display: none !important;
    }
    
    /* Make drawer static and full width for printing */
    & [class*="MuiDrawer-paper"] {
      position: static !important;
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      max-height: none !important;
      background: white !important;
      box-shadow: none !important;
      border: none !important;
    }
    
    /* Hide drawer container background effects */
    & [class*="DrawerContainer"] {
      background: white !important;
      backdrop-filter: none !important;
    }
    
    /* A4 page settings for printing */
    @page {
      size: A4;
      margin: 0.5cm;
    }
    
    /* Ensure proper print styling */
    body {
      background: white !important;
    }
    
    /* Page break controls */
    h1, h2, h3, h4, h5, h6 {
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    
    table {
      page-break-inside: avoid;
    }
    
    img {
      page-break-inside: avoid;
      max-width: 100%;
    }
    
    /* Prevent orphaned content */
    p, li {
      orphans: 3;
      widows: 3;
    }
  }
`

const ActionButton = styled(Button)`
  min-width: auto;
  padding: 8px 12px;
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
  gap: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(67, 56, 202, 0.2);
  }
`

export default function ReportViewDrawer({
  open,
  onClose,
  bookedSlot,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [auxiliaryData, setAuxiliaryData] = useState(null)
  const [bookedEventInfo, setBookedEventInfo] = useState(null)
  
  // Lock body scroll when drawer is open
  useBodyScrollLock(open, 1300)
  const [emrSectionDataList, setEmrSectionDataList] = useState([])
  const [sendingReport, setSendingReport] = useState(false)
  const [downloadingReport, setDownloadingReport] = useState(false)
  const therapyReportPreviewRef = useRef(null)

  useEffect(() => {
    if (open && bookedSlot?.slug) {
      fetchReportData()
    } else {
      // Reset state when drawer closes
      setAuxiliaryData(null)
      setBookedEventInfo(null)
      setError(null)
      setEmrSectionDataList([])
    }
  }, [open, bookedSlot?.slug])

  const fetchReportData = async () => {
    if (!bookedSlot?.slug) {
      setError('No booked slot slug available')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Try support portal API first (for support portal context)
      let response = null
      try {
        const supportResponse = await getSPDataWithParams(
          '/api/secure/cc/support/practitioner-supervisor/booked-slot/details',
          {
            slug: bookedSlot.slug,
            'send-auxiliary-data': true,
          }
        )
        
        if (supportResponse?.status === 200 && isCommonResponseSuccessfulV2(supportResponse)) {
          const data = getDataMapFromCommonResponse(supportResponse)
          setBookedEventInfo(data?.bookedEvent)
          setEmrSectionDataList(data?.screening__EmrSectionData || data?.emrSectionDataList || data?.emrSectionData || [])
          setAuxiliaryData(data)
          setLoading(false)
          return
        }
      } catch (supportErr) {
        // Fall back to regular API if support API fails
        console.log('Support API failed, trying regular API:', supportErr)
      }

      // Fallback to regular session service API
      response = await sessionService.getBookedEventDetails(bookedSlot.slug, true)
      
      if (response.success) {
        setBookedEventInfo(response.bookedEvent)
        setAuxiliaryData(response.auxiliaryData)
      } else {
        setError(response.errorData?.message || 'Failed to load report data')
      }
    } catch (err) {
      console.error('Error fetching report data:', err)
      setError('An error occurred while loading the report')
    } finally {
      setLoading(false)
    }
  }

  const therapyReportData = auxiliaryData?.therapyReport
    ? {
        report: auxiliaryData?.therapyReport,
        template: auxiliaryData?.therapyReportTemplate,
      }
    : null

  const screeningReportData = auxiliaryData?.screeningReport
    ? {
        report: auxiliaryData?.screeningReport,
        template: auxiliaryData?.screeningReportTemplate,
      }
    : null

  const hasReport = therapyReportData || screeningReportData

  const handlePrint = () => {
    // For therapy reports, use the ref's print method which handles the print properly
    if (therapyReportData && therapyReportPreviewRef.current?.print) {
      therapyReportPreviewRef.current.print()
    } else {
      // For screening reports or fallback, use window.print with proper styling
      // Add a class to body to help with print styling
      document.body.classList.add('printing-report')
      window.print()
      // Remove class after print dialog closes
      setTimeout(() => {
        document.body.classList.remove('printing-report')
      }, 1000)
    }
  }

  const handleDownload = async () => {
    if (!bookedEventInfo?.id) {
      toast.error('Unable to download: Missing event ID')
      return
    }
    
    // Determine report type
    let reportType = null
    if (therapyReportData) {
      reportType = 'therapy-report'
    } else if (screeningReportData) {
      reportType = 'screening-report'
    } else {
      toast.error('Unable to download: No report found')
      return
    }
    
    setDownloadingReport(true)
    try {
      const token = localStorage.getItem('token')
      const countryCode = getCountryCode()
      const mainLanguageCode = getMainLanguageCode()
      
      // Use support portal API
      const endpoint = '/api/secure/cc/support/practitioner/emr-reports/download'
      
      const response = await axios.get(`${supportApiURL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
        params: {
          reportType: reportType,
          eventId: bookedEventInfo.id,
          languageCode: mainLanguageCode,
          platform: 'web',
          countryCode: countryCode,
        },
        responseType: 'blob',
      })
      
      if (response?.status === 200 && response.data) {
        const contentDisposition = response.headers?.['content-disposition']
        let filename = `${reportType}.pdf`
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '')
          }
        }
        
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        
        toast.success('Report downloaded successfully')
      } else {
        toast.error('Failed to download report')
      }
    } catch (error) {
      console.error('Error downloading report:', error)
      toast.error('Failed to download report. Please try again.')
    } finally {
      setDownloadingReport(false)
    }
  }

  const handleSendReport = async () => {
    if (!bookedSlot?.slug && !bookedEventInfo?.id) return
    
    setSendingReport(true)
    try {
      const bookedEventId = bookedEventInfo?.id || bookedSlot?.id
      
      if (!bookedEventId) {
        alert('Unable to send report: Missing event ID')
        setSendingReport(false)
        return
      }
      
      // Determine reportType based on which report exists
      let reportType = null
      if (therapyReportData) {
        reportType = 'therapy-report'
      } else if (screeningReportData) {
        reportType = 'screening-report'
      } else {
        alert('Unable to send report: No report type found')
        setSendingReport(false)
        return
      }
      
      // Use unified endpoint: /api/secure/cc/support/practitioner-supervisor/emr-reports/{reportType}/send-to-triggering-practitioner
      // Query params: eventId OR reportId
      const apiSignature = `/api/secure/cc/support/practitioner-supervisor/emr-reports/${reportType}/send-to-triggering-practitioner`
      
      // Try support portal API first
      try {
        const response = await postSPDataWithParams(
          apiSignature,
          null,
          {
            eventId: bookedEventId,
          }
        )
        
        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          alert('Report sent successfully')
          setSendingReport(false)
          return
        }
      } catch (supportErr) {
        console.log('Support API failed:', supportErr)
        alert('Failed to send report. Please try again.')
      }
    } catch (err) {
      console.error('Error sending report:', err)
      alert('An error occurred while sending the report')
    } finally {
      setSendingReport(false)
    }
  }
  
  // Get report title based on report type
  const getReportTitle = () => {
    if (therapyReportData) return 'Therapy Report'
    if (screeningReportData) return 'Screening Report'
    return 'View Report'
  }

  return (
    <PrintOnlyContainer>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '800px',
            maxWidth: isMobile ? '100%' : '90vw',
            height: isMobile ? '90vh' : '100%',
            maxHeight: isMobile ? '90vh' : '100%',
            borderTopLeftRadius: isMobile ? '20px' : 0,
            borderTopRightRadius: isMobile ? '20px' : 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 255, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            '@media print': {
              position: 'static',
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              maxHeight: 'none',
              background: 'white',
              boxShadow: 'none',
              border: 'none',
            },
          },
        }}
        ModalProps={{
          disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook
        }}
      >
        <DrawerContainer>
          {/* Drag Handle for Mobile Bottom Sheet */}
          {isMobile && (
            <Box
              sx={{
                width: '40px',
                height: '4px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '2px',
                margin: '8px auto',
                cursor: 'grab',
                '&:active': {
                  cursor: 'grabbing',
                },
              }}
            />
          )}
        <DrawerHeader className="report-drawer-header">
          <DrawerTitle>
            <Description sx={{ fontSize: 20 }} />
            {getReportTitle()}
          </DrawerTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasReport && (
              <ActionButtonsContainer className="report-drawer-actions">
                <Tooltip title="Print Report">
                  <ActionButton
                    variant="outlined"
                    size={isMobile ? 'medium' : 'small'}
                    startIcon={<Print />}
                    onClick={handlePrint}
                    sx={{
                      borderColor: 'rgba(67, 56, 202, 0.3)',
                      color: 'rgba(67, 56, 202, 0.9)',
                      minHeight: isMobile ? '44px' : '36px',
                      padding: isMobile ? '10px 16px' : '6px 12px',
                      '&:hover': {
                        borderColor: 'rgba(67, 56, 202, 0.5)',
                        background: 'rgba(67, 56, 202, 0.05)',
                      },
                    }}
                  >
                    Print
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Download as PDF">
                  <ActionButton
                    variant="outlined"
                    size={isMobile ? 'medium' : 'small'}
                    startIcon={<Download />}
                    onClick={handleDownload}
                    disabled={downloadingReport}
                    sx={{
                      borderColor: 'rgba(67, 56, 202, 0.3)',
                      color: 'rgba(67, 56, 202, 0.9)',
                      minHeight: isMobile ? '44px' : '36px',
                      padding: isMobile ? '10px 16px' : '6px 12px',
                      '&:hover': {
                        borderColor: 'rgba(67, 56, 202, 0.5)',
                        background: 'rgba(67, 56, 202, 0.05)',
                      },
                      '&:disabled': {
                        opacity: 0.6,
                      },
                    }}
                  >
                    {downloadingReport ? 'Downloading...' : 'Download'}
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Send to Triggering Practitioner">
                  <span>
                    <ActionButton
                      variant="outlined"
                      size={isMobile ? 'medium' : 'small'}
                      startIcon={<Send />}
                      onClick={handleSendReport}
                      disabled={sendingReport}
                      sx={{
                        borderColor: 'rgba(67, 56, 202, 0.3)',
                        color: 'rgba(67, 56, 202, 0.9)',
                        minHeight: isMobile ? '44px' : '36px',
                        padding: isMobile ? '10px 16px' : '6px 12px',
                        '&:hover': {
                          borderColor: 'rgba(67, 56, 202, 0.5)',
                          background: 'rgba(67, 56, 202, 0.05)',
                        },
                        '&:disabled': {
                          opacity: 0.6,
                        },
                      }}
                    >
                      {sendingReport ? 'Sending...' : 'Send'}
                    </ActionButton>
                  </span>
                </Tooltip>
              </ActionButtonsContainer>
            )}
            <IconButton
              onClick={onClose}
              sx={{
                color: 'rgba(67, 56, 202, 0.7)',
                padding: isMobile ? '12px' : '8px',
                minWidth: isMobile ? '48px' : '40px',
                minHeight: isMobile ? '48px' : '40px',
                '&:hover': {
                  background: 'rgba(67, 56, 202, 0.1)',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DrawerHeader>

        <DrawerContent>
          {loading ? (
            <LoadingContainer>
              <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
              <Typography variant="body2" color="text.secondary">
                Loading report...
              </Typography>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </ErrorContainer>
          ) : !hasReport ? (
            <ErrorContainer>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No report available for this appointment.
              </Alert>
            </ErrorContainer>
          ) : (
            <>
              {screeningReportData && (
                <ReportContainer className="report-container">
                  <PrintableScreeningReportComponent
                    emrSectionDataList={emrSectionDataList}
                    bookedEventInfo={bookedEventInfo}
                    templateData={screeningReportData?.template}
                    isAlwaysPrinting
                    report={screeningReportData?.report}
                    hideDownloadButton={true}
                    hideSendToDoctorButton={true}
                    hideHeader={true}
                    hideHeadingText={true}
                    sendReportToTriggeringPractitionerApiSignature="/api/secure/cc/support/practitioner-supervisor/emr-reports/screening-report/send-to-triggering-practitioner"
                  />
                </ReportContainer>
              )}
              {therapyReportData && (
                <ReportContainer className="report-container">
                  <TherapyReportPreview
                    ref={therapyReportPreviewRef}
                    hideInUI={false}
                    isAlwaysPrinting
                    report={therapyReportData?.report}
                    templateData={therapyReportData?.template}
                    setReport={() => {}}
                    hideDownloadButton={true}
                    hideSendToDoctorButton={true}
                    hideHeader={true}
                    hideHeadingText={true}
                    sendReportToTriggeringPractitionerApiSignature="/api/secure/cc/support/practitioner-supervisor/emr-reports/therapy-report/send-to-triggering-practitioner"
                  />
                </ReportContainer>
              )}
            </>
          )}
        </DrawerContent>
      </DrawerContainer>
    </Drawer>
    </PrintOnlyContainer>
  )
}
